import { Option, SelectOption } from '@common/types/common';
import { ISignUpAPIFormValues, Roles } from '@common/types/user';
import { ErrorFrame } from '@components/utility/forms/ErrorFrame/ErrorFrame';
import { Select } from '@components/utility/selects';
import { GOOGLE_RECAPTCHA_SITE_KEY } from '@constants/common';
import { useLocaleFormRevalidation } from '@hooks/useLocaleFormRevalidation';
import { usePrefixedNavigate } from '@hooks/usePrefixedNavigate';
import {
    Button,
    CircularProgress,
    InputLabel,
    ListItemText,
    SelectChangeEvent,
    TextField,
    Typography,
} from '@mui/material';
import { createSignUpValidationSchema } from '@schemas/Authentication/SignUpSchema';
import { useSignUpMutation } from '@services/accounts';
import { useGetCountriesQuery } from '@services/countries';
import { useGetLanguagesQuery } from '@services/translation';
import { handleError } from '@utils/common';
import { useFormik } from 'formik';
import { useCallback, useMemo, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

const FALLBACK_LANGUAGES: SelectOption[] = [{ value: 'en', label: 'English' }];

const SignUpForm = () => {
    const { formatMessage, locale } = useIntl();
    const SignupSchema = useMemo(() => createSignUpValidationSchema(formatMessage), [formatMessage, locale]);
    const roleOptions: Option[] = [
        { label: formatMessage({ id: 'common.roles.endUser', defaultMessage: 'End User' }), value: 'end_user' },
        { label: formatMessage({ id: 'common.roles.installer', defaultMessage: 'Installer' }), value: 'installer' },
        {
            label: formatMessage({ id: 'common.roles.distributor', defaultMessage: 'Distributor' }),
            value: 'distributor',
        },
    ];

    const [signUp] = useSignUpMutation();
    const navigate = usePrefixedNavigate();
    const recaptcha = useRef<ReCAPTCHA>(null);
    const recaptchaErrorMessage = formatMessage({
        id: 'auth.signUp.errors.recaptchaFailed',
        defaultMessage: 'reCAPTCHA verification failed. Please try again.',
    });

    const formik = useFormik<Omit<ISignUpAPIFormValues, 'requested_role'> & { requested_role: '' | Roles }>({
        initialValues: {
            first_name: '',
            last_name: '',
            email: '',
            company_name: '',
            country: '',
            language: '',
            requested_role: '',
            recaptcha_token: '',
        },
        validationSchema: SignupSchema,
        onSubmit: async (values) => {
            try {
                await signUp({
                    ...values,
                    requested_role: values.requested_role ? values.requested_role : Roles.END_USER,
                    language: values.language ? values.language : 'en',
                }).unwrap();
                navigate(
                    { pathname: '/auth/signup-success', search: window.location.search },
                    { state: { 'sign-up-allow': true } }
                );
            } catch (error: unknown) {
                handleError(
                    formatMessage({ id: 'auth.signUp.errors.signUpFailed', defaultMessage: 'Error signing up' }),
                    error
                );
            }
        },
    });

    useLocaleFormRevalidation(formik, locale);

    const handleSelectChange = (event: SelectChangeEvent<unknown>, _child?: React.ReactNode) => {
        const { name, value } = event.target as { name: string; value: unknown };
        formik.setFieldValue(name, value as string);
    };
    const setRecaptchaToken = (token: string | null) => {
        formik.setFieldTouched('recaptcha_token', true, false);
        formik.setFieldValue('recaptcha_token', token ?? '', true);
    };

    const handleRecaptchaChange = (token: string | null) => {
        setRecaptchaToken(token);
        if (token) {
            formik.setFieldError('recaptcha_token', undefined);
        }
    };

    const handleRecaptchaFailure = () => {
        setRecaptchaToken(null);
        formik.setFieldError('recaptcha_token', recaptchaErrorMessage);
        handleError(
            formatMessage({
                id: 'auth.signUp.errors.signUpFailed',
                defaultMessage: 'Error signing up',
            }),
            new Error('reCAPTCHA verification failed before sign up submission.')
        );
    };

    const renderLanguageOption = useCallback(
        ({ label, value }: SelectOption) => (
            <ListItemText
                slotProps={{
                    primary: {
                        className: '!flex items-center gap-2',
                    },
                }}
            >
                <img
                    src={`/static/flags/${
                        value
                            ? value.toString().toLowerCase() === 'en'
                                ? 'us_uk'
                                : value.toString().toLowerCase()
                            : 'victron'
                    }.svg`}
                    alt={value ? `Flag of ${label}` : 'Victron flag'}
                    className="w-[27px] h-[15px] rounded-sm"
                />
                <Typography>{label}</Typography>
            </ListItemText>
        ),
        []
    );

    const { data: countries, isLoading: isCountriesLoading } = useGetCountriesQuery();

    const { data: languages = FALLBACK_LANGUAGES, isLoading: isLanguagesLoading } = useGetLanguagesQuery();

    return (
        <div className="w-full overflow-auto">
            <form onSubmit={formik.handleSubmit} className="space-y-6" noValidate>
                {/* First name and Last name */}
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1">
                        <InputLabel htmlFor="firstName" className="block text-sm font-medium text-neutral-950 mb-2">
                            <FormattedMessage
                                id="common.fields.firstName.title"
                                defaultMessage="First Name"
                                description="Label for first name input field"
                            />
                        </InputLabel>
                        <TextField
                            id="firstName"
                            name="first_name"
                            value={formik.values.first_name}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.first_name && Boolean(formik.errors.first_name)}
                            helperText={formik.touched.first_name && formik.errors.first_name}
                            fullWidth
                            size="small"
                            placeholder={formatMessage({
                                id: 'common.fields.firstName.placeholder',
                                defaultMessage: 'Your First Name',
                            })}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '0.5rem',
                                },
                            }}
                        />
                    </div>
                    <div className="flex-1">
                        <InputLabel htmlFor="lastName" className="block text-sm font-medium text-neutral-950 mb-2">
                            <FormattedMessage
                                id="common.fields.lastName.title"
                                defaultMessage="Last Name"
                                description="Label for last name input field"
                            />
                        </InputLabel>
                        <TextField
                            id="lastName"
                            name="last_name"
                            value={formik.values.last_name}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.last_name && Boolean(formik.errors.last_name)}
                            helperText={formik.touched.last_name && formik.errors.last_name}
                            fullWidth
                            size="small"
                            placeholder={formatMessage({
                                id: 'common.fields.lastName.placeholder',
                                defaultMessage: 'Your Last Name',
                            })}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '0.5rem',
                                },
                            }}
                        />
                    </div>
                </div>
                <div>
                    <InputLabel htmlFor="email" className="block text-sm font-medium text-neutral-950 mb-2">
                        <FormattedMessage
                            id="common.fields.email.addressTitle"
                            defaultMessage="Email Address"
                            description="Label for email address input field"
                        />
                    </InputLabel>
                    <TextField
                        type="email"
                        id="email"
                        name="email"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.email && Boolean(formik.errors.email)}
                        helperText={formik.touched.email && formik.errors.email}
                        fullWidth
                        size="small"
                        placeholder={formatMessage({
                            id: 'common.fields.email.placeholder',
                            defaultMessage: 'Your email address',
                        })}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '0.5rem',
                            },
                        }}
                    />
                </div>
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1">
                        <InputLabel htmlFor="company" className="block text-sm font-medium text-neutral-950 mb-2">
                            <FormattedMessage
                                id="common.fields.company.title"
                                defaultMessage="Company"
                                description="Label for company input field"
                            />
                        </InputLabel>
                        <TextField
                            type="text"
                            id="company"
                            name="company_name"
                            value={formik.values.company_name}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.company_name && Boolean(formik.errors.company_name)}
                            helperText={formik.touched.company_name && formik.errors.company_name}
                            fullWidth
                            size="small"
                            placeholder={formatMessage({
                                id: 'common.fields.company.placeholder',
                                defaultMessage: 'Your company',
                            })}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '0.5rem',
                                },
                            }}
                        />
                    </div>
                    <div className="flex-1">
                        <InputLabel htmlFor="role" className="block text-sm font-medium text-neutral-950 mb-2">
                            <FormattedMessage
                                id="common.fields.role.title"
                                defaultMessage="Role"
                                description="Label for role selection field"
                            />
                        </InputLabel>
                        <Select
                            id="role"
                            type="radio"
                            name="requested_role"
                            size="smallest"
                            value={formik.values.requested_role}
                            onChange={handleSelectChange}
                            onBlur={formik.handleBlur}
                            displayEmpty
                            fullWidth
                            sx={{
                                borderRadius: '0.5rem',
                            }}
                            items={roleOptions}
                            error={formik.touched.requested_role && !!formik.errors.requested_role}
                            helperText={
                                formik.touched.requested_role &&
                                formik.errors.requested_role &&
                                formik.errors.requested_role
                            }
                            placeholder={formatMessage({
                                id: 'common.fields.role.placeholder',
                                defaultMessage: 'Select your role',
                            })}
                        />
                    </div>
                </div>
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1">
                        <InputLabel htmlFor="country" className="block text-sm font-medium text-neutral-950 mb-2">
                            <FormattedMessage
                                id="common.fields.country.title"
                                defaultMessage="Country"
                                description="Label for country selection field"
                            />
                        </InputLabel>
                        <Select
                            id="country"
                            type="radio"
                            name="country"
                            size="smallest"
                            value={formik.values.country}
                            onChange={handleSelectChange}
                            onBlur={formik.handleBlur}
                            displayEmpty
                            fullWidth
                            sx={{
                                borderRadius: '0.5rem',
                            }}
                            searchable
                            virtualize
                            items={countries ?? []}
                            loading={isCountriesLoading}
                            error={formik.touched.country && !!formik.errors.country}
                            helperText={formik.touched.country && formik.errors.country && formik.errors.country}
                            placeholder={formatMessage({
                                id: 'common.fields.country.placeholder',
                                defaultMessage: 'Select your country',
                            })}
                            autoClose
                        />
                    </div>
                    <div className="flex-1">
                        <InputLabel htmlFor="language" className="block text-sm font-medium text-neutral-950 mb-2">
                            <FormattedMessage
                                id="common.fields.language.title"
                                defaultMessage="Language"
                                description="Label for language selection field"
                            />
                        </InputLabel>
                        <Select
                            items={languages ?? []}
                            type="radio"
                            name="language"
                            fullWidth
                            value={formik.values.language ?? ''}
                            onChange={handleSelectChange}
                            loading={isLanguagesLoading}
                            size="smallest"
                            error={formik.touched.language && !!formik.errors.language}
                            helperText={formik.touched.language && formik.errors.language && formik.errors.language}
                            renderOption={renderLanguageOption}
                            placeholder={formatMessage({
                                id: 'common.fields.language.placeholder',
                                defaultMessage: 'Select your language',
                            })}
                        />
                    </div>
                </div>
                <ErrorFrame
                    error={!!formik.touched.recaptcha_token && !!formik.errors.recaptcha_token}
                    helperText={formik.touched.recaptcha_token && formik.errors.recaptcha_token}
                    className="w-fit ml-auto"
                >
                    <ReCAPTCHA
                        sitekey={GOOGLE_RECAPTCHA_SITE_KEY}
                        ref={recaptcha}
                        onChange={handleRecaptchaChange}
                        onExpired={() => setRecaptchaToken(null)}
                        onErrored={handleRecaptchaFailure}
                    />
                </ErrorFrame>
                <div className="flex flex-col gap-3">
                    <Button
                        type="submit"
                        disabled={formik.isSubmitting}
                        className="rounded-full h-min md:min-w-[200px]"
                        startIcon={formik.isSubmitting ? <CircularProgress size={20} /> : null}
                        fullWidth
                    >
                        <span>
                            <FormattedMessage
                                id="common.signUp"
                                defaultMessage="Sign Up"
                                description="Text for sign up button"
                            />
                        </span>
                    </Button>
                    <Button
                        component={Link}
                        to={{ pathname: '../login', search: window.location.search }}
                        variant="outlined"
                        className="w-full h-10 px-4 rounded-full border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400 transition-colors duration-200 focus:ring-gray-500"
                    >
                        <FormattedMessage
                            id="common.goBack"
                            defaultMessage="Go Back"
                            description="Text for go back button"
                        />
                    </Button>
                </div>
            </form>
        </div>
    );
};

export { SignUpForm };
